/**
 * sqlite3mt — Multi-Worker SQLite Architecture v3.1.22
 *
 * Foundational types from §96 §1 (Kärnmodell).
 *
 * These types are the normative contract for Root identity, public history,
 * page-count-aware deltas, and all inter-worker state.
 */

// ── §1.1 Public history ────────────────────────────────────────

/** Canonical hash used as root identifier. */
export type RootId = string & { __brand: 'RootId' }

/**
 * Immutable root descriptor for a database snapshot.
 *
 * §1.1: rootId is the canonical identity; all comparisons use sameRoot().
 * meta.pageCount is the logical page count at this root.
 */
export interface Root {
  rootId: RootId
  meta: {
    /** SHA-256 of the canonical public manifest root node. §104 §5.3: manifestRootId. */
    manifestRootId: string
    pageCount: number
    parentPublicRootId: RootId | null
  }
}

/**
 * A specific versioned point in the public linear history.
 *
 * version increases strictly monotonically with each successful publish.
 */
export interface PublicHead {
  root: Root
  version: bigint
}

export interface Sqlite3MtDebugStagePayload {
  kind: string
  stage: string | null
  remoteVersion: string
  localVersion: string
  overlaySize: number
}

/**
 * Normative root equality — §1.1, R17, R51.
 *
 * All root comparisons in runtime, journal, snapshot, intent, CAS,
 * and remote-head logic MUST use this function over canonical rootId.
 * Never use JavaScript object identity (===) or ad-hoc deep comparison.
 */
export function sameRoot(a: Root, b: Root): boolean {
  return a.rootId === b.rootId
}

// ── §1.2 Local views ───────────────────────────────────────────

/** Page number type (1-based, as in SQLite). */
export type Pgno = number & { __brand: 'Pgno' }

/** Content hash for a single page image. */
export type PageHash = string & { __brand: 'PageHash' }

/** Frame offset within WAL (commit-frame index). */
export type FrameMax = number & { __brand: 'FrameMax' }

// ── §1.3 Page-count-aware deltas ───────────────────────────────

/**
 * A page-count-aware protocol delta.
 *
 * §1.3: All protocol deltas carry explicit postPageCount.
 *
 * Semantics:
 * - pageHashes: final page image hashes per affected page
 * - postPageCount = null: transaction does not change logical EOF
 * - postPageCount = N: after this transaction, logical EOF is exactly N pages
 *
 * For WAL-backed committed transactions, postPageCount MUST be derived
 * from the commit-frame's nPage field, not from opportunistic page-1 parsing.
 */
export interface Delta {
  pageHashes: Map<Pgno, PageHash>
  postPageCount: number | null
}

// ── §1.4 WAL integration ──────────────────────────────────────

/** WAL salt pair identifying a specific WAL cycle. */
export interface WalSalt {
  salt0: number
  salt1: number
}

/**
 * Snapshot of WAL state at a point in time.
 * Used for shrink/reset detection and frontier comparison.
 * Must never be compared across different salts.
 */
export interface WalSnapshot {
  salt0: number
  salt1: number
  iChange: number
  mxFrame: number
  nBackfill: number
}

/** Check whether two WAL salts match. */
export function sameWalSalt(
  a0: number | null,
  a1: number | null,
  b0: number | null,
  b1: number | null
): boolean {
  return a0 === b0 && a1 === b1
}

// ── §5 Control Worker volatile state (subset: gates & busy flags) ──

/** Reconcile intent phases — §6.4. */
export type ReconcileIntentPhase = 'PREPARED' | 'APPENDING' | 'COMMITTED'

/** Checkpoint mode (§16.1). */
export type CheckpointMode = 'PASSIVE' | 'RESTART' | 'TRUNCATE'

// ── §7.10 WAL scan result types ────────────────────────────────

/**
 * A single WAL frame parsed from the durabla WAL file.
 * Internal type used by scanWalCommittedTransactions to build transactions.
 */
export interface WalFrameEntry {
  /** 1-based frame index within the WAL. */
  frameIdx: number
  /** Page number stored in this frame. */
  pgno: number
  /**
   * DB page count after this commit (= commit-frame's nTruncate/nPage).
   * 0 for mid-transaction spill frames; >0 only on the commit frame.
   */
  nPage: number
  /** Raw page data (pageSize bytes). */
  pageData: Uint8Array
}

/**
 * A committed WAL transaction recovered by scanning.
 * §7.10: emitted by scanWalCommittedTransactions.
 */
export interface CommittedTx {
  frameMax: FrameMax
  delta: Delta
  blobs: Map<PageHash, Uint8Array>
}

/** Result of scanExactCommittedTransaction. */
export type ExactScanResult =
  | { kind: 'FOUND_EXACT_MATCH'; frameMax: FrameMax }
  | { kind: 'NO_MATCH' }
  | { kind: 'CORRUPT' }

/**
 * A raw committed WAL transaction before blob hydration.
 * Used internally by scanWalCommittedTransactions.
 * The caller provides the page→hash function externally.
 */
export interface RawCommittedTx {
  /** Index of the commit frame (the last frame in this transaction). */
  frameMax: number
  /** All frames in this transaction, in WAL order. */
  frames: WalFrameEntry[]
  /**
   * postPageCount from the commit frame's nPage field.
   * §1.3 / R46: MUST be derived from commit-frame metadata, not from Page 1.
   */
  postPageCount: number
}

// ── §4.1 SAB1 slot states ──────────────────────────────────────

/** Slot states for SAB1 page-cache — §4.1. */
export const enum SlotState {
  EMPTY = 0,
  FETCHING = 1,
  READY = 2,
  ERROR = 3,
  RETRY = 4,
}

// ── §15.2 MergePlan ────────────────────────────────────────────

/**
 * Result of buildMergePlan(§15.3) — the three-way merge plan that drives
 * remote reconcile. Every field is derived from base/local/remote state.
 *
 * The merge plan captures:
 * - which pages changed remotely vs locally
 * - the resolved result page hashes after merge
 * - the resolved result page count (via resolveMergedPageCount)
 * - the import blobs needed for the reconcile import WAL commit
 */
export interface MergePlan {
  baseRoot: Root
  baseVersion: bigint
  basePageCount: number

  remoteRoot: Root
  remoteVersion: bigint
  remotePageCount: number

  walSalt0: number
  walSalt1: number
  caughtUpFrameMax: number

  /** Normalized local page hashes (sparse overlay resolved against base). */
  liveLocalNorm: Map<Pgno, PageHash>
  liveLocalPageCount: number

  /** Pages that differ between baseRoot and remoteRoot. */
  remoteChanged: Set<Pgno>
  /** Page hashes from remoteRoot (null if page > remotePageCount). */
  remoteHashes: Map<Pgno, PageHash | null>
  /** Page hashes from baseRoot (null if page > basePageCount). */
  baseHashes: Map<Pgno, PageHash | null>

  /** Final merged page hashes (only non-base entries). */
  resultHashByPgno: Map<Pgno, PageHash>
  /** Resolved page count from three-way merge. */
  resultPageCount: number

  /** Page blobs to import, keyed by pgno. */
  importBlobsByPgno: Map<Pgno, Uint8Array>
  /** Page blobs to import, keyed by content hash (dedup). */
  importBlobsByHash: Map<PageHash, Uint8Array>

  /** Cache for base pages fetched during merge. */
  basePagesCache: Map<Pgno, Uint8Array>
  /** Cache for remote pages fetched during merge. */
  remotePagesCache: Map<Pgno, Uint8Array>
}

/**
 * Abstract interface for SecureBlob v3.1 manifest operations (§12).
 *
 * buildMergePlan uses this interface to resolve page hashes from roots
 * without depending on the concrete transport implementation.
 * The runtime provides a concrete implementation backed by
 * DurableLocalBlobStore + rpcGetBlob/rpcGetManifestNode.
 */
export interface ManifestOperations {
  /** Get the page hash at pgno from a concrete root. Returns null if pgno > root.meta.pageCount. */
  getPageHashFromConcreteRoot(root: Root, pgno: Pgno): Promise<PageHash | null>

  /** Get the page data bytes by content hash. */
  getPageBytesByHash(hash: PageHash): Promise<Uint8Array | null>

  /** Optional debug hook used to surface merge-plan progress. */
  setDebugStage?(stage: string): void

  /** Compute the logical sqlite3mt page hash for raw page bytes. */
  hashPage?(data: Uint8Array): PageHash

  /** Compute the set of pages that differ between two concrete roots. */
  diffConcreteRoots(a: Root, b: Root): Promise<Set<Pgno>>
}

// ── §15.7 Import try result ────────────────────────────────────

/**
 * Metadata returned by appendWalFramesSingleTransaction — the WAL-layer
 * primitive that physically appends frames to SAB2 and returns the resulting
 * salt and commit frame position.
 */
export interface AppendWalFramesMeta {
  commitFrameMax: number
  postWalSalt0: number
  postWalSalt1: number
}

/**
 * Result of tryImportPreparedIntentAsSingleWalCommit (§15.7).
 *
 * - OK: import succeeded (appendMeta describes the physical commit;
 *       null when no WAL write was needed)
 * - LOCK_BUSY: WAL_WRITE lock could not be acquired
 * - WAL_CAPACITY_BUSY: import would exceed the local WAL shadow before
 *       APPENDING was made durable; caller should checkpoint and retry
 * - STALE_TIP_GROW: live WAL grew beyond caughtUpFrameMax (retry loop)
 * - STALE_EPOCH: WAL salt changed (restart reconcile)
 * - STALE_RESET: live WAL shrank below caughtUpFrameMax (restart reconcile)
 */
export type ImportTryResult =
  | { kind: 'OK'; appendMeta: AppendWalFramesMeta | null }
  | { kind: 'LOCK_BUSY' }
  | {
      kind: 'WAL_CAPACITY_BUSY'
      currentWalBytes: number
      requiredWalBytes: number
      maxWalBytes: number
      frameCount: number
    }
  | { kind: 'STALE_TIP_GROW' }
  | { kind: 'STALE_EPOCH' }
  | { kind: 'STALE_RESET' }

// ── §15.8 Reconcile catch-up probe state ──────────────────────

/**
 * Snapshot of WAL + frontier state used by the reconcile orchestrator
 * (§15.9) to detect orphaned/unintegrable WAL tails.
 */
export interface ReconcileProbeState {
  salt0: number
  salt1: number
  liveMxFrame: number
  integratedFrameMax: number
}

// ── §11.1.3 WAL frame constants ─────────────────────────────────

/**
 * WAL frame header layout constants.
 *
 * Each WAL frame has a 24-byte header followed by pageSize bytes of page data:
 *   offset 0: page number (4 bytes, big-endian uint32)
 *   offset 4: commit size / nPage (4 bytes, big-endian uint32)
 *   offset 8: salt-1 copy (4 bytes)
 *   offset 12: salt-2 copy (4 bytes)
 *   offset 16: checksum-1 (4 bytes)
 *   offset 20: checksum-2 (4 bytes)
 *
 * The WAL file header is 32 bytes. Frames start at byte 32.
 */
export const WAL_HEADER_SIZE = 32
export const WAL_FRAME_HEADER_SIZE = 24

/** Total bytes per WAL frame (header + page data). */
export function walFrameSize(pageSize: number): number {
  return WAL_FRAME_HEADER_SIZE + pageSize
}

/** Byte offset of the first frame in the WAL file. */
export function walFrameOffset(frameNo: number, pageSize: number): number {
  // frameNo is 1-based; frame 1 starts at byte 32
  return WAL_HEADER_SIZE + (frameNo - 1) * walFrameSize(pageSize)
}

// ── §11.1.3 Committed page extraction ───────────────────────────

/**
 * Result of extracting committed pages from SAB2 WAL region.
 *
 * §11.1.3: extractCommittedPagesAndCommitSizeBackwardCopy reads frames
 * from txStartFrame through pendingCommitFrame in SAB2.
 *
 * - pages: raw page data keyed by pgno (later frames overwrite earlier)
 * - commitDbSizePages: nPage from the commit frame (= logical EOF)
 */
export interface ExtractedCommitPages {
  pages: Map<Pgno, Uint8Array>
  commitDbSizePages: number
}

// ── §11.1.3 Client Worker → Control Worker commit sync message ─

/**
 * wal-commit-sync message sent from Client Worker to Control Worker.
 *
 * §11.1.3: After a WAL commit frame is written, the Client Worker
 * extracts page data, computes hashes, and posts this message.
 *
 * The message carries the runtimeEpoch for generation safety (R42).
 * blobs are transferred (not cloned) to avoid copying.
 */
export interface WalCommitSyncMessage {
  type: 'wal-commit-sync'
  runtimeEpoch: bigint
  connId: number
  reqId: bigint
  /**
   * Gate wait-sequences captured immediately before the first WAL byte was
   * written. They prove that this commit was admitted before any later gate
   * closure observed by the Control Worker.
   */
  admittedWriteGateSeq?: number
  admittedCheckpointGateSeq?: number
  walSalt0: number
  walSalt1: number
  frameMax: number
  delta: Delta
  blobs: Map<PageHash, Uint8Array>
}

// ── §11.1 Client Worker per-connection state ───────────────────

/**
 * Reader-visible frontier captured for the current MainDb read path.
 *
 * §8.1 + §10.3: xReadMainDb must wait against the exact WAL cycle/frame
 * frontier the reader observed before it touches SAB1 slots or posts a fault.
 */
export interface ReaderVisibleFrontier {
  /** WAL salt pair visible to the reader for the current read. */
  readerSalt0: number
  readerSalt1: number
  /** Highest WAL frame visible to the reader for the current read. */
  readerFrameMax: number
}

/**
 * Per-connection mutable state for a Client Worker.
 *
 * §11.1: Each Client Worker connection tracks its runtime epoch,
 * reader-visible frontier, connection id, commit sequence counter,
 * and WAL write state.
 */
export interface ClientWorkerConnState extends ReaderVisibleFrontier {
  /** The runtime epoch this connection was attached to. Stale epoch = fatal. */
  attachedRuntimeEpoch: bigint
  /** Connection slot index in SAB4 per-connection arrays. */
  connId: number
  /** Monotonic commit request counter (64-bit, R40). */
  nextCommitReqId: bigint
  /**
   * Gate wait-sequences captured for the current WAL transaction admission.
   * Null means no WAL bytes for the current transaction have crossed the
   * local write/checkpoint gates yet.
   */
  admittedWriteGateSeq: number | null
  admittedCheckpointGateSeq: number | null
  /**
   * The pending commit frame number (0 = no commit pending).
   * Set to the commit frame's frameNo when a WAL write completes a commit.
   */
  pendingCommitFrame: number
  /**
   * First frame of the current transaction.
   * Initialized only immediately before the first WAL byte is written.
   */
  txStartFrame: number
  /** Whether this connection currently holds WAL_WRITE. */
  holdsWalWrite: boolean
  /** Whether this connection has entered fatal I/O mode. */
  fatalIoMode: boolean
  /**
   * Current SHM lock state for this connection (8 slots).
   * -1 = not held, 0 = shared, 1 = exclusive.
   * §11.1: used by allowedMask() during xShmBarrier (§17).
   */
  myLocks: Int8Array
}

// ── §5 Control Worker state helpers ────────────────────────────

/**
 * Full Control Worker mutable state for DB-view and workspace-view.
 * §5 + §9: used as the `this` argument for overlay/delta helpers.
 */
export interface ControlWorkerState {
  // DB-view
  dbBaseRoot: Root
  dbOverlay: Map<Pgno, PageHash>
  dbPageCount: number

  // Workspace-view
  workspaceBaseHead: PublicHead
  workspaceOverlay: Map<Pgno, PageHash>
  workspacePageCount: number

  // WAL integration
  pendingDbDeltas: Map<FrameMax, Delta>
  currentWalSalt0: number | null
  currentWalSalt1: number | null
  integratedFrameMax: number
  lastWalSnapshot: WalSnapshot | null
  localCommitSeq: bigint
}
